(() => {
  const EMBED_SELECTOR = 'shopify-forms-embed';
  const FORM_SELECTOR = 'form[data-testid="form"]';
  const FORM_GAP_SELECTOR = 'form[data-testid="form"][class*="_formFieldset_"]';
  const FORM_GAP_PX = '30px';
  const FIELD_LABEL_SELECTOR = 'label[class*="_formInputFieldLabel_"]';
  const SELECT_LABEL_SELECTOR = 'div[class*="_selectContainer_"] > p[class*="_label_"]';
  const SELECT_TOGGLE_TEXT_SELECTOR = 'button[class*="_selectToggle_"] > span[class*="_selectToggleText_"]';
  const STYLE_ID = 'c-shopify-forms-style';
  const SUBMITTED_CLASS = 'is-forms-submitted';
  const FILLED_STATE_CLASS_PARTIAL = 'filled';
  const PARAGRAPH_SELECTOR = 'p';
  const STYLE_TAG_NAME = 'style';
  const WHITE_SPACE_PRE_LINE = 'pre-line';
  const SHADOW_ROOT_RETRY_DELAY_MS = 100;
  const MAX_SHADOW_ROOT_RETRY_COUNT = 20;
  const VISUALLY_HIDDEN_SIZE_PX = '1px';
  const LABEL_COLOR_PROPERTY = '--c-shopify-forms-label-color';
  const LABEL_FONT_SIZE_PROPERTY = '--c-shopify-forms-label-font-size';
  const initializedHosts = new WeakSet();
  const pendingHosts = new Set();

  if (window.cShopifyFormsReady) return;

  window.cShopifyFormsReady = true;

  /** Shadow DOM内に適用するスタイル。 */
  const getEmbedStyle = () => `
    ${FORM_GAP_SELECTOR} {
      gap: ${FORM_GAP_PX};
    }

    ${SELECT_LABEL_SELECTOR} {
      position: absolute;
      width: ${VISUALLY_HIDDEN_SIZE_PX};
      height: ${VISUALLY_HIDDEN_SIZE_PX};
      padding: 0;
      margin: -${VISUALLY_HIDDEN_SIZE_PX};
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    ${SELECT_TOGGLE_TEXT_SELECTOR} {
      flex: 1;
      text-align: left;
    }

    ${SELECT_TOGGLE_TEXT_SELECTOR}:empty::before {
      content: attr(data-placeholder);
      color: var(${LABEL_COLOR_PROPERTY});
      font-size: var(${LABEL_FONT_SIZE_PROPERTY});
    }
  `;

  /** Shadow rootの生成完了を短時間だけ待つ。 */
  const waitForShadowRoot = async (host) => {
    for (let retryCount = 0; retryCount < MAX_SHADOW_ROOT_RETRY_COUNT; retryCount += 1) {
      if (host.shadowRoot) return host.shadowRoot;

      await new Promise((resolve) => {
        window.setTimeout(resolve, SHADOW_ROOT_RETRY_DELAY_MS);
      });
    }

    return host.shadowRoot;
  };

  /** 未入力のテキストフィールドラベルの書式をプレースホルダーへ反映する。 */
  const setPlaceholderStyles = (host, shadowRoot) => {
    const fieldLabels = [...shadowRoot.querySelectorAll(FIELD_LABEL_SELECTOR)];
    const label = fieldLabels.find(
      (fieldLabel) => !fieldLabel.className.toLowerCase().includes(FILLED_STATE_CLASS_PARTIAL),
    )
      || fieldLabels[0];

    if (!label) return;

    const computedStyle = window.getComputedStyle(label);

    if (host.style.getPropertyValue(LABEL_COLOR_PROPERTY) !== computedStyle.color) {
      host.style.setProperty(LABEL_COLOR_PROPERTY, computedStyle.color);
    }

    if (host.style.getPropertyValue(LABEL_FONT_SIZE_PROPERTY) !== computedStyle.fontSize) {
      host.style.setProperty(LABEL_FONT_SIZE_PROPERTY, computedStyle.fontSize);
    }
  };

  /** 選択前のトグルにアクセシブルなラベルを表示する。 */
  const setSelectPlaceholder = (shadowRoot) => {
    const selectLabel = shadowRoot.querySelector(SELECT_LABEL_SELECTOR);
    const toggleText = shadowRoot.querySelector(SELECT_TOGGLE_TEXT_SELECTOR);
    const labelText = selectLabel?.textContent?.trim();

    if (!toggleText || !labelText || toggleText.textContent.trim()) return;

    if (toggleText.dataset.placeholder !== labelText) {
      toggleText.dataset.placeholder = labelText;
    }
  };

  /** 送信完了文を文単位で改行する。 */
  const formatSuccessMessage = (shadowRoot) => {
    shadowRoot.querySelectorAll(PARAGRAPH_SELECTOR).forEach((paragraph) => {
      const textWalker = document.createTreeWalker(paragraph, NodeFilter.SHOW_TEXT);
      const textNodes = [];
      let textNode = textWalker.nextNode();

      while (textNode) {
        textNodes.push(textNode);
        textNode = textWalker.nextNode();
      }

      textNodes.forEach((node) => {
        const originalText = node.nodeValue;
        const formattedText = originalText.replace(/。(?=.)/g, '。\n');

        if (formattedText !== originalText) {
          node.nodeValue = formattedText;
        }
      });

      if (paragraph.style.whiteSpace !== WHITE_SPACE_PRE_LINE) {
        paragraph.style.whiteSpace = WHITE_SPACE_PRE_LINE;
      }
    });
  };

  /** Shadow rootの表示状態を整える。 */
  const initShadowRoot = (host, shadowRoot) => {
    const ensureEmbedStyle = () => {
      if (shadowRoot.querySelector(`#${STYLE_ID}`)) return;

      const style = document.createElement(STYLE_TAG_NAME);

      style.id = STYLE_ID;
      style.textContent = getEmbedStyle();
      shadowRoot.append(style);
    };

    let hasSeenForm = false;
    let shadowRootObserver;

    const applyEmbedState = () => {
      ensureEmbedStyle();

      const form = shadowRoot.querySelector(FORM_SELECTOR);

      if (form) {
        hasSeenForm = true;
        setPlaceholderStyles(host, shadowRoot);
        setSelectPlaceholder(shadowRoot);
        return;
      }

      if (!hasSeenForm) return;

      if (!document.documentElement.classList.contains(SUBMITTED_CLASS)) {
        document.documentElement.classList.add(SUBMITTED_CLASS);
      }

      formatSuccessMessage(shadowRoot);
      shadowRootObserver?.takeRecords();
    };

    applyEmbedState();

    shadowRootObserver = new MutationObserver(applyEmbedState);

    shadowRootObserver.observe(shadowRoot, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  };

  /** フォーム埋め込みを初期化する。 */
  const initEmbed = async (host) => {
    if (initializedHosts.has(host) || pendingHosts.has(host)) return;

    pendingHosts.add(host);

    const shadowRoot = await waitForShadowRoot(host);

    pendingHosts.delete(host);

    if (!shadowRoot || initializedHosts.has(host)) return;

    initializedHosts.add(host);
    initShadowRoot(host, shadowRoot);
  };

  /** 追加されたフォーム埋め込みを検出する。 */
  const initEmbeds = () => {
    document.querySelectorAll(EMBED_SELECTOR).forEach((host) => {
      void initEmbed(host);
    });
  };

  const documentObserver = new MutationObserver(initEmbeds);

  documentObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEmbeds);
  } else {
    initEmbeds();
  }
})();
